import React, {useEffect, useState, useRef} from 'react';
import {
	Card,
	CardHeader,
	CardContent,
	Table,
	TableHead,
	TableRow,
	TableCell,
	TableBody,
	Typography,
	Button,
	makeStyles, Box,
} from '@material-ui/core';
import {Add as AddIcon} from '@material-ui/icons';
import Loader from '../../../../components/Loader/Loader';
import OrderItemRow from './OrderItemRow';
import ProductSelectionModal from './ProductSelectionModal';
import ValidationErrorSummary from './ValidationErrorSummary';
import {useOrderItems, useOrderTotals} from '../hooks/useOrderItems';

const useStyles = makeStyles((theme) => ({
	loaderContainer: {
		position: 'fixed',
		left: '55%',
		top: '35%',
	},
	addProductButton: {
		borderStyle: 'dashed',
		marginTop: 16,
	},
	headerRow: {
		backgroundColor: '#f5f5f5',
	},
	totalRow: {
		backgroundColor: '#f5f5f5',
	},
	totalPrice: {
		fontWeight: 'bold',
		fontSize: '1.1rem',
	},
	replaceButton: {
		backgroundColor: '#8BC34A',
		color: 'white',
		textTransform: 'none',
		padding: '4px 12px',
		fontSize: '0.75rem',
		marginRight: theme.spacing(0.5),
		'&:hover': {
			backgroundColor: '#7CB342',
		},
	},
	cancelButton: {
		backgroundColor: '#f44336',
		color: 'white',
		textTransform: 'none',
		padding: '4px 12px',
		fontSize: '0.75rem',
		marginRight: theme.spacing(0.5),
		'&:hover': {
			backgroundColor: '#d32f2f',
		},
	},
	deleteIcon: {
		color: '#999',
		padding: '4px',
	},
	actionButtonsContainer: {
		display: 'grid',
		gridTemplateColumns: 'repeat(3, 1fr)',
		gap: theme.spacing(1),
		gridAutoRows: 'minmax(40px, auto)',
		marginTop: theme.spacing(2),
		paddingTop: theme.spacing(1),
		maxWidth: 500,
		marginLeft: 'auto',
	},
	recalculateButton: {
		gridColumn: '1 / 3',
		gridRow: 1,
		backgroundColor: '#2196F3',
		color: 'white',
		textTransform: 'none',
		fontSize: '0.8rem',
		fontWeight: 500,
		borderRadius: 4,
		padding: '6px 12px',
		'&:hover': {
			backgroundColor: '#1976D2',
		},
	},
	cancelOrderButton: {
		gridColumn: 3,
		gridRow: '1 / 3',
		backgroundColor: '#f44336',
		color: 'white',
		textTransform: 'none',
		fontSize: '0.85rem',
		fontWeight: 600,
		borderRadius: 4,
		padding: '6px 12px',
		'&:hover': {
			backgroundColor: '#d32f2f',
		},
	},
	discardButton: {
		gridColumn: 1,
		gridRow: 2,
		backgroundColor: '#9E9E9E',
		color: 'white',
		textTransform: 'none',
		fontSize: '0.8rem',
		fontWeight: 500,
		borderRadius: 4,
		padding: '6px 12px',
		'&:hover': {
			backgroundColor: '#757575',
		},
	},
	saveButton: {
		gridColumn: '2 / 3',
		gridRow: 2,
		backgroundColor: '#8BC34A',
		color: 'white',
		textTransform: 'none',
		fontSize: '0.8rem',
		fontWeight: 500,
		borderRadius: 4,
		padding: '6px 12px',
		'&:hover': {
			backgroundColor: '#7CB342',
		},
	},
}));

const TABLE_HEADERS = (showUnitPrice) => [
	{label: 'SKU', align: 'left'},
	{label: 'Item Name', align: 'left'},
	{label: 'Quantity', align: 'center'},
	{label: 'Weight', align: 'left'},
	{label: 'Unit Price', align: 'left'},
	{label: 'Coupon Discount', align: 'left'},
	{label: 'Price', align: 'left'},
	{label: 'Vat Rate', align: 'left'},
	{label: 'DeliPoints Earned', align: 'left'},
	{label: 'DeliPoints Redeemed', align: 'left'},
	{label: 'DeliPoints Multiplier', align: 'left'},
	{label: 'Fix Points Promote', align: 'left'},
	{label: 'Status', align: 'left'},
	{label: 'Cancellation Reason', align: 'left'},
	{label: 'Comment (visible for customer)', align: 'left'},
	{label: 'Actions', align: 'center'},
];

const OrderItemTable = ({
	                        details,
	                        storeItem,
	                        colors,
	                        driverTip,
	                        onDriverTipChange,
	                        deliveryFee,
	                        onDeliveryFeeChange,
	                        refreshOrder,
	                        onTotalsChange,
	                        onOrderSummeryChange,
	                        orderCouponDiscount,
	                        onOrderCouponDiscountChange,
	                        selectedCouponId,
	                        calculatedTotals,
	                        pendingAddress,
	                        onAddressSaved,
	                        onForceHasChanges,
	                        onHasChangesChange,
	                        recalculateHandlerRef,
	                        saveHandlerRef,
	                        discardHandlerRef
                        }) => {
	const classes = useStyles();

	// Extract order ID from details
	const orderId = details?.order_id || details?.id;

	// Use custom hooks
	const {
		orderItems,
		hasChanges,
		isSaving,
		isLoading,
		isRecalculating,
		lastSaved,
		validationErrors,
		fetchOrderDetails,
		handlers,
		utils,
		forceHasChanges
	} = useOrderItems(details, orderId, driverTip, onDriverTipChange, deliveryFee, onDeliveryFeeChange, refreshOrder, pendingAddress, onAddressSaved, selectedCouponId, onTotalsChange, onOrderSummeryChange);
	const {totalQuantity, totalPrice, calculateItemTotal} = useOrderTotals(orderItems);

	// Store recalculate handler in ref for parent to trigger auto-recalculate
	useEffect(() => {
		if (recalculateHandlerRef && handlers.handleRecalculate) {
			recalculateHandlerRef.current = handlers.handleRecalculate;
		}
	}, [recalculateHandlerRef, handlers.handleRecalculate]);

	// Store save/discard handlers in refs for parent unsaved-changes modal
	useEffect(() => {
		if (saveHandlerRef) saveHandlerRef.current = handlers.handleSave;
		if (discardHandlerRef) discardHandlerRef.current = handlers.handleDiscard;
	}, [saveHandlerRef, discardHandlerRef, handlers.handleSave, handlers.handleDiscard]);

	// Product selection modal state
	const [showProductModal, setShowProductModal] = useState(false);
	const [itemToReplace, setItemToReplace] = useState(null);
	const [modalMode, setModalMode] = useState('replace'); // 'replace' or 'add'

	// Handle replace button click - open modal
	const handleReplaceClick = (item) => {
		setItemToReplace(item);
		setModalMode('replace');
		setShowProductModal(true);
	};

	// Handle add product button click - open modal
	const handleAddProductClick = () => {
		setItemToReplace(null);
		setModalMode('add');
		setShowProductModal(true);
	};

	// Handle product selection from modal
	const handleProductSelected = async (product, quantity) => {
		if (modalMode === 'replace') {
			// Replace mode
			if (itemToReplace && product) {
				await handlers.handleReplaceItem(itemToReplace, product);
				setShowProductModal(false);
				setItemToReplace(null);
			}
		} else if (modalMode === 'add') {
			// Add mode
			if (product) {
				await handlers.handleAddProduct(product, quantity);
				setShowProductModal(false);
			}
		}
	};

	// Handle modal close
	const handleCloseModal = () => {
		setShowProductModal(false);
		setItemToReplace(null);
		setModalMode('replace');
	};

	// Fetch order details on component mount (optional - only if you want fresh data)
	useEffect(() => {
		// Uncomment to fetch fresh order details on mount
		// if (orderId) {
		// 	fetchOrderDetails();
		// }
	}, [orderId, fetchOrderDetails]);

	// Expose forceHasChanges to parent component
	useEffect(() => {
		if (onForceHasChanges && forceHasChanges) {
			onForceHasChanges(forceHasChanges);
		}
	}, [onForceHasChanges, forceHasChanges]);

	// Sync hasChanges (dirty flag) up to parent
	useEffect(() => {
		if (onHasChangesChange) {
			onHasChangesChange(hasChanges);
		}
	}, [onHasChangesChange, hasChanges]);

	// Track previous totals to avoid unnecessary updates
	const prevTotalsRef = useRef({item_total: null, coupon_discount_amount: null});
	const isInitialMountRef = useRef(true);

	// Notify parent when calculated totals change (only after user makes changes)
	useEffect(() => {
		if (onTotalsChange && hasChanges) {
			// Calculate coupon discount from active items
			const activeItems = orderItems.filter((item) => item.status !== 'cancelled');
			const couponDiscount = activeItems.reduce(
				(sum, item) => sum + (parseFloat(item.coupon_discount_amount) || 0),
				0
			);

			// Only update if values actually changed
			if (
				prevTotalsRef.current.item_total !== totalPrice ||
				prevTotalsRef.current.coupon_discount_amount !== couponDiscount
			) {
				prevTotalsRef.current = {item_total: totalPrice, coupon_discount_amount: couponDiscount};
				onTotalsChange({
					item_total: totalPrice,
					coupon_discount_amount: couponDiscount,
				});
			}
		}
		isInitialMountRef.current = false;
	}, [totalPrice, orderItems, onTotalsChange, hasChanges]);

	// Handle when order-level coupon discount is removed (set to 0)
	const prevOrderCouponDiscountRef = useRef(orderCouponDiscount);
	useEffect(() => {
		// If orderCouponDiscount changed to 0 from a non-zero value, clear all item discounts
		if (orderCouponDiscount === 0 && prevOrderCouponDiscountRef.current !== 0 && prevOrderCouponDiscountRef.current !== null) {
			handlers.handleClearAllCouponDiscounts();
		}
		prevOrderCouponDiscountRef.current = orderCouponDiscount;
	}, [orderCouponDiscount, handlers]);

	// Check if any item has original_price > 0 to show Unit Price column
	const showUnitPrice = orderItems.some(item =>
		item.original_price && parseFloat(item.original_price) > 0
	);

	// Loading state
	if (!details || isLoading) {
		return (
			<div className={classes.loaderContainer}>
				<Loader color={colors?.primary || '#007bff'} h="100"/>
			</div>
		);
	}

	return (
		<Card className="mb-3">
			<CardHeader
				title={
					<Typography variant="h6">
						<b>ORDER ITEM DETAILS</b>
					</Typography>
				}
			/>
			<CardContent className="overflow-auto">
				{/* Validation Error Summary */}
				<ValidationErrorSummary
					validationErrors={validationErrors}
					orderItems={orderItems}
				/>

				<Table>
					{/* Table Header */}
					<TableHead>
						<TableRow className={classes.headerRow}>
							{TABLE_HEADERS(showUnitPrice).map((header, index) => (
								<TableCell key={index} align={header.align}>
									<b>{header.label}</b>
								</TableCell>
							))}
						</TableRow>
					</TableHead>

					{/* Table Body */}
					<TableBody>
						{/* Order Items - API returns items sorted: original followed by replacements */}
						{orderItems.map((order, index) => {
							// Check if this is a replacement item (show "Replaced by:" label before it)
							const isReplacement = order.replaced_from !== null;
							// Check if previous item was the original (to show label only once)
							const prevItem = index > 0 ? orderItems[index - 1] : null;
							const showReplacedByLabel = isReplacement &&
								prevItem &&
								prevItem.order_item_id === order.replaced_from?.original_order_item_id;

							return (
								<React.Fragment key={order.id}>
									{/* Replaced by label */}
									{showReplacedByLabel && (
										<TableRow>
											<TableCell colSpan={16} style={{
												padding: '4px 16px',
												backgroundColor: '#e8f5e9',
												borderLeft: '3px solid #4caf50'
											}}>
												<Typography variant="caption" style={{
													fontWeight: 'bold',
													color: '#2e7d32'
												}}>
													↳ Replaced by:
												</Typography>
											</TableCell>
										</TableRow>
									)}
									{/* Item Row */}
									<OrderItemRow
										order={order}
										storeItem={storeItem}
										calculateItemTotal={calculateItemTotal}
										handlers={{
											...handlers,
											handleReplaceItem: () => handleReplaceClick(order)
										}}
										getFieldError={utils?.getFieldError}
										showUnitPrice={showUnitPrice}
									/>
								</React.Fragment>
							);
						})}

						{/* Add Product Button Row */}
						<TableRow>
						<TableCell colSpan={16}>
								<Button
									variant="outlined"
									startIcon={<AddIcon/>}
									onClick={handleAddProductClick}
									className={classes.addProductButton}
									fullWidth
								>
									Add product
								</Button>
							</TableCell>
						</TableRow>

						{/* Total Row */}
						<TableRow className={classes.totalRow}>
							<TableCell colSpan={2}>
								<b>Total</b>
							</TableCell>
							<TableCell align="center">
								<b>{totalQuantity}</b>
							</TableCell>
							<TableCell colSpan={3}/>
							<TableCell>
								{(() => {
									const activeItems = orderItems.filter((item) => item.status !== 'cancelled');

									// Calculate total coupon discount from item-level discounts
									const itemLevelCouponDiscount = activeItems.reduce(
										(sum, item) => sum + (parseFloat(item.coupon_discount_amount) || 0),
										0
									);

									// Determine total discount to display:
									// Priority: item-level discounts → calculatedTotals (if > 0) → orderCouponDiscount → saved order-level discount
									let totalCouponDiscount = itemLevelCouponDiscount;

									if (itemLevelCouponDiscount === 0) {
										// No item-level discounts - check for order-level discount
										// Priority: calculatedTotals (from recalculate if > 0) → orderCouponDiscount (from state) → saved values
										if (calculatedTotals?.coupon_discount_amount !== undefined && calculatedTotals.coupon_discount_amount > 0) {
											totalCouponDiscount = calculatedTotals.coupon_discount_amount;
										} else if (orderCouponDiscount > 0) {
											// Use orderCouponDiscount from state (includes initial load from details)
											totalCouponDiscount = orderCouponDiscount;
										} else if (calculatedTotals?.coupon_discount_amount === 0) {
											// Explicitly set to 0 after recalculate (coupon removed)
											totalCouponDiscount = 0;
										} else {
											// Fall back to saved order-level discount (for backwards compatibility)
											totalCouponDiscount = parseFloat(details?.order_payment_summery?.coupon_discount_amount)
												|| parseFloat(details?.coupon_discount_amount)
												|| parseFloat(details?.coupon_discount)
												|| 0;
										}
									}

									const finalTotal = totalPrice - totalCouponDiscount;

									return totalCouponDiscount > 0 ? (
										<Box>
											<Typography style={{textDecoration: 'line-through', color: '#999', fontSize: '0.9rem'}}>
												$ {totalPrice.toFixed(2)}
											</Typography>
											<Typography className={classes.totalPrice} style={{color: '#4caf50'}}>
												$ {finalTotal.toFixed(2)}
											</Typography>
										</Box>
									) : (
										<Typography className={classes.totalPrice}>
											$ {totalPrice.toFixed(2)}
										</Typography>
									);
								})()}
							</TableCell>
							<TableCell colSpan={10}/>
						</TableRow>
					</TableBody>
				</Table>
				{/* Action Buttons - Grid Layout */}
				<Box className={classes.actionButtonsContainer}>
					<Button
						variant="contained"
						className={classes.recalculateButton}
						onClick={handlers.handleRecalculate}
						disabled={isSaving || isRecalculating || isLoading}
					>
						{isRecalculating ? 'Recalculating...' : 'Recalculate order total'}
					</Button>
					<Button
						variant="contained"
						className={classes.cancelOrderButton}
						onClick={handlers.handleCancelOrder}
						disabled={isSaving || isRecalculating || isLoading}
					>
						Cancel order
					</Button>
					<Button
						variant="contained"
						className={classes.discardButton}
						onClick={handlers.handleDiscard}
						disabled={!hasChanges || isSaving || isRecalculating || isLoading}
					>
						Discard changes{hasChanges && ' (*)'}
					</Button>
					<Button
						variant="contained"
						className={classes.saveButton}
						id="order-save-btn"
						onClick={handlers.handleSave}
						disabled={!hasChanges || isSaving || isRecalculating || isLoading}
					>
						{isSaving ? 'Saving...' : 'Save'}{hasChanges && !isSaving && ' (*)'}
					</Button>
				</Box>
				{/* Status indicators */}
				{hasChanges && !isSaving && (
					<Box style={{textAlign: 'right', marginTop: 8, fontSize: '0.85rem', color: '#f57c00'}}>
						<Typography variant="caption">* You have unsaved changes</Typography>
					</Box>
				)}
				{lastSaved && (
					<Box style={{textAlign: 'right', marginTop: 4, fontSize: '0.75rem', color: '#666'}}>
						<Typography variant="caption">
							Last saved: {new Date(lastSaved).toLocaleString()}
						</Typography>
					</Box>
				)}
			</CardContent>

			{/* Product Selection Modal for Replace/Add */}
			<ProductSelectionModal
				open={showProductModal}
				onClose={handleCloseModal}
				onSelectProduct={handleProductSelected}
				currentItem={itemToReplace}
				mode={modalMode}
			/>
		</Card>
	);
};

export default OrderItemTable;
